import { CheckCircle2, MapPin, RadioTower, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api, type BatchResult } from "../api";
import type { Location } from "../types";

export function BatchPage() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState("");
  const [batch, setBatch] = useState<BatchResult | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    api.locations().then(setLocations);
    const stored = sessionStorage.getItem("parallax-batch-token");
    if (stored) {
      api.getBatch(stored).then(setBatch).catch(() => sessionStorage.removeItem("parallax-batch-token"));
    } else {
      api.getCurrentBatch().then(setBatch).catch(() => undefined);
    }
  }, []);

  async function start() {
    const next = await api.createBatch(locationId);
    setBatch(next);
    if (next.token) sessionStorage.setItem("parallax-batch-token", next.token);
  }

  async function refresh() {
    const token = sessionStorage.getItem("parallax-batch-token");
    setBatch(token ? await api.getBatch(token) : await api.getCurrentBatch());
  }

  async function confirm() {
    const token = sessionStorage.getItem("parallax-batch-token");
    if (!batch || !window.confirm(`Move ${batch.count} records to ${batch.destination.name}?`)) return;
    const result = token ? await api.confirmBatch(token) : await api.confirmCurrentBatch();
    sessionStorage.removeItem("parallax-batch-token");
    setBatch(null);
    setMessage(`${result.updated} inventory records moved to ${batch.destination.name}.`);
  }

  return (
    <div className="page narrow-page">
      <div className="page-heading">
        <div>
          <h1>Batch location mode</h1>
          <p>Choose one location, then scan inventory tags one after another with this iPhone.</p>
        </div>
      </div>
      {!batch ? (
        <section className="batch-start">
          <label>
            Destination location
            <select value={locationId} onChange={(event) => setLocationId(event.target.value)}>
              <option value="">Select location</option>
              {(["origin", "transit", "destination"] as const).map((group) => (
                <optgroup key={group} label={group}>
                  {locations
                    .filter((location) => location.group === group)
                    .map((location) => (
                      <option value={location.id} key={location.id}>
                        {location.name}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </label>
          <button className="button primary full" disabled={!locationId} onClick={start}>
            <RadioTower size={20} /> Start batch
          </button>
        </section>
      ) : (
        <>
          <div className="batch-banner">
            <MapPin />
            <div>
              <span>Moving scanned items to</span>
              <strong>{batch.destination.name}</strong>
            </div>
            <strong>{batch.count}</strong>
          </div>
          <div className="batch-instruction">
            <RadioTower size={30} />
            <h2>Scan the next NFC tag</h2>
            <p>The batch remains active while Safari opens each tag. Duplicate scans are ignored.</p>
            <button className="button secondary" onClick={refresh}>
              Refresh review list
            </button>
          </div>
          <div className="batch-review">
            {batch.entries.map((entry) => (
              <div key={entry.id}>
                <span>
                  <strong>{entry.stock_number}</strong>
                  <small>{entry.name} · {entry.current_location || "No location"}</small>
                </span>
                <button
                  className="icon-button"
                  onClick={async () => {
                    const token = sessionStorage.getItem("parallax-batch-token");
                    setBatch(
                      token
                        ? await api.removeBatchEntry(token, entry.id)
                        : await api.removeCurrentBatchEntry(entry.id)
                    );
                  }}
                  aria-label={`Remove ${entry.stock_number} from batch`}
                >
                  <Trash2 size={19} />
                </button>
              </div>
            ))}
          </div>
          <button className="button primary full" disabled={!batch.count} onClick={confirm}>
            <CheckCircle2 /> Confirm {batch.count} location updates
          </button>
        </>
      )}
      {message && <div className="alert success">{message}</div>}
    </div>
  );
}
